/**
 * B631 (b, read half) — the public REST wire for tenants who build their own
 * storefront. A CLOSED table maps resource URLs onto the buyer operations the
 * platform's own storefront already calls — a second WIRE over the same
 * registry, not a second backend (the OCTO wire's pattern, B526 (f1)). It is
 * data the authorizer (which op), the ops entry (how to translate), the stack
 * (which routes exist) and the OpenAPI generator (what to document) all read:
 * four readers, one table, no drift. Not an operation module: the registry
 * does not collect it.
 *
 * The caller is the storefront's own guest: `POST /v1/sessions` is the public
 * guest door (session.guest — tenant by header, rate-walled), and its token
 * is the Bearer every read carries, exactly as on `/ops`. Every route keeps
 * every /ops wall. Tenant API keys with scopes are leg (a), a separate
 * credential; nothing here mints or reads one.
 *
 * `path` names the path parameters, `query` the query parameters and `body`
 * the body fields the op takes; anything else the caller sends never reaches
 * the strict contract. The tenant never rides a parameter — the header and
 * the bearer carry it.
 */
export type RestWireTag = "Sessions" | "Products" | "Availability";

export interface RestWireRoute {
  readonly routeKey: string;
  readonly operation: string;
  readonly tag: RestWireTag;
  /** One line for the reference, in a storefront builder's words. */
  readonly summary: string;
  readonly path?: readonly string[];
  readonly query?: readonly string[];
  readonly body?: readonly string[];
}

export const REST_WIRE_ROUTES: readonly RestWireRoute[] = Object.freeze([
  {
    routeKey: "POST /v1/sessions",
    operation: "session.guest",
    tag: "Sessions",
    summary: "Start a guest session on one of your channels — the token is the Bearer for every other call",
    body: ["channelId"],
  },
  {
    routeKey: "GET /v1/products",
    operation: "catalog.list",
    tag: "Products",
    summary: "List the products a channel sells, with variants and display prices",
    query: ["channelId", "versionId"],
  },
  {
    routeKey: "GET /v1/products/{productId}",
    operation: "catalog.get",
    tag: "Products",
    summary: "Get one product as a channel sells it",
    path: ["productId"],
    query: ["channelId", "versionId"],
  },
  {
    routeKey: "GET /v1/products/{productId}/calendar",
    operation: "product.calendar",
    tag: "Availability",
    summary: "Get a product's \"from\" prices for a date range, per day or per showtime",
    path: ["productId"],
    query: ["channelId", "from", "to"],
  },
  {
    routeKey: "GET /v1/schedules/{scheduleId}/slots",
    operation: "slot.list",
    tag: "Availability",
    summary: "List the dated slots (showtimes, departures, entry times) a schedule runs",
    path: ["scheduleId"],
    query: ["serviceId", "from", "to"],
  },
  {
    routeKey: "GET /v1/slots/{slotId}",
    operation: "slot.get",
    tag: "Availability",
    summary: "Get one slot: when it starts and ends and whether it is on sale",
    path: ["slotId"],
  },
  {
    routeKey: "GET /v1/slots/{slotId}/availability",
    operation: "slot.availability",
    tag: "Availability",
    summary: "How many places are left in a slot, per capacity pool",
    path: ["slotId"],
  },
]);

const BY_KEY = new Map(REST_WIRE_ROUTES.map((r) => [r.routeKey, r]));

export function restWireRouteFor(routeKey: string): RestWireRoute | undefined {
  return BY_KEY.get(routeKey);
}
